// Team colours that stay visible on the background they are drawn on.
// A colour too close to its background is lightened (dark backgrounds) or darkened (light ones)
// just enough to stand out; colours that already read well are left untouched.

// backgrounds the app draws team colours on
export const BACKGROUNDS = {
    white: '#ffffff',
    black: '#000000',
    // the charcoal of the league's cards and of the live stream overlays
    dark: '#232323'
}

// contrast needed (WCAG ratio): coloured shapes need less than text
const MIN_CONTRAST = 2.2

export function parseColor(value) {
    if (!value || typeof value !== 'string') return null
    const color = value.trim()
    let match = color.match(/^#?([0-9a-f]{3})$/i)
    if (match) {
        const [r, g, b] = match[1].split('').map(c => parseInt(c + c, 16))
        return { r, g, b }
    }
    match = color.match(/^#?([0-9a-f]{6})$/i)
    if (match) {
        const n = parseInt(match[1], 16)
        return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 }
    }
    match = color.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i)
    if (match) return { r: Number(match[1]), g: Number(match[2]), b: Number(match[3]) }
    return null
}

export function toHex({ r, g, b }) {
    const part = n => Math.round(Math.max(0, Math.min(255, n))).toString(16).padStart(2, '0')
    return `#${part(r)}${part(g)}${part(b)}`
}

function luminance({ r, g, b }) {
    const channel = (v) => {
        const c = v / 255
        return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4
    }
    return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b)
}

export function contrast(a, b) {
    const ca = typeof a === 'string' ? parseColor(a) : a
    const cb = typeof b === 'string' ? parseColor(b) : b
    if (!ca || !cb) return 21
    const la = luminance(ca)
    const lb = luminance(cb)
    return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05)
}

function mix(color, target, amount) {
    return {
        r: color.r + (target.r - color.r) * amount,
        g: color.g + (target.g - color.g) * amount,
        b: color.b + (target.b - color.b) * amount
    }
}

// the colour itself when it stands out from the background, otherwise the closest shade that does
export function readableOn(value, background = BACKGROUNDS.white, minContrast = MIN_CONTRAST) {
    const color = parseColor(value)
    const bg = parseColor(background)
    if (!color || !bg) return value
    if (contrast(color, bg) >= minContrast) return value
    // away from the background: lighter on dark ones, darker on light ones
    const target = luminance(bg) < 0.18 ? { r: 255, g: 255, b: 255 } : { r: 0, g: 0, b: 0 }
    for (let amount = 0.05; amount <= 1; amount += 0.05) {
        const candidate = mix(color, target, amount)
        if (contrast(candidate, bg) >= minContrast) return toHex(candidate)
    }
    return toHex(target)
}

// whether the colour had to be changed to be seen (worth an outline as well)
export function isFaintOn(value, background = BACKGROUNDS.white, minContrast = MIN_CONTRAST) {
    const color = parseColor(value)
    const bg = parseColor(background)
    return Boolean(color && bg && contrast(color, bg) < minContrast)
}

// rough perceptual distance between two colours (0 = same)
function distance(a, b) {
    const rMean = (a.r + b.r) / 2
    const dr = a.r - b.r
    const dg = a.g - b.g
    const db = a.b - b.b
    return Math.sqrt((2 + rMean / 256) * dr * dr + 4 * dg * dg + (2 + (255 - rMean) / 256) * db * db)
}

export function areSimilar(a, b) {
    const ca = parseColor(a)
    const cb = parseColor(b)
    return Boolean(ca && cb && distance(ca, cb) < 120)
}

// The colours of the two teams of a match on a given background: each one visible on it,
// and the second one swapped for a neutral when the two would look alike
export function teamColorsOn(color1, color2, background = BACKGROUNDS.white, minContrast = MIN_CONTRAST) {
    const bg = parseColor(background)
    const dark = bg ? luminance(bg) < 0.18 : false
    const fallback1 = '#037ade'
    const fallback2 = dark ? '#ffffff' : '#232323'
    const team1 = readableOn(color1 || fallback1, background, minContrast)
    let team2 = readableOn(color2 || fallback2, background, minContrast)
    if (areSimilar(team1, team2)) {
        // the alternative must also differ from the first team (a blue team keeps the dark / white one)
        const alternatives = dark ? ['#ffffff', '#03e5b7'] : ['#232323', '#037ade']
        team2 = alternatives.find(alt => !areSimilar(team1, alt)) || alternatives[0]
    }
    return { team1, team2 }
}

// The colour of a team on the live stream: its match colour (brand colour), else its team colour
export function matchColor(team) {
    return (team && (team.brandColor || team.color)) || null
}

// The match colours of the two teams on a background, each visible on it and told apart from the other
export function getMatchColors(team1, team2, background = BACKGROUNDS.white, minContrast) {
    return teamColorsOn(matchColor(team1), matchColor(team2), background, minContrast)
}
